import React from "react";
import { Users, Dumbbell, Flame } from "lucide-react";

/**
 * Showcase with three mini product screenshots/panels.
 */
export default function DashboardShowcase() {
  return (
    <section className="py-20 md:py-28 relative" data-testid="dashboard-showcase">
      <div className="section-container">
        <div className="text-center max-w-2xl mx-auto mb-14 reveal">
          <div className="chip mb-4">A peek inside</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            Designed for <span className="gradient-text">daily coaching use</span>
          </h2>
          <p className="mt-4 text-white/60">Fast, focused, and built for real trainers — on desktop and on the go.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Panel 1: Clients */}
          <ShowcaseCard
            icon={Users}
            title="Clients at a glance"
            desc="Every client's goal, plan, and next session — in one searchable list."
            testId="showcase-clients"
            inner={<ClientsPanel />}
          />
          {/* Panel 2: Workout builder */}
          <ShowcaseCard
            icon={Dumbbell}
            title="Workout builder"
            desc="Drag-and-drop exercises, sets, reps & rest periods. Reuse templates across clients."
            testId="showcase-workout"
            inner={<WorkoutPanel />}
          />
          {/* Panel 3: Diet planner (UPGRADED copy) */}
          <ShowcaseCard
            icon={Flame}
            title="Your exact fat-loss diet. No confusion."
            desc="Personalised, macro-aware plans your clients actually stick to — no guesswork."
            testId="showcase-diet"
            inner={<DietPanel />}
          />
        </div>
      </div>
    </section>
  );
}

function ShowcaseCard({ icon: Icon, title, desc, testId, inner }) {
  return (
    <div className="card-surface rounded-2xl p-5 reveal flex flex-col" data-testid={testId}>
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(59,130,246,0.15))",
            border: "1px solid rgba(34,211,238,0.3)",
          }}
        >
          <Icon size={16} className="text-cyan-300" />
        </div>
        <div className="font-bold text-white leading-tight text-[15px]">{title}</div>
      </div>
      <p className="text-sm text-white/55 mb-4">{desc}</p>
      <div className="rounded-xl bg-[#070D1B] border border-white/5 overflow-hidden flex-1">
        {inner}
      </div>
    </div>
  );
}

function ClientsPanel() {
  const clients = [
    { name: "Aarav Mehta", plan: "Fat loss · Beginner", progress: 72, init: "AM" },
    { name: "Priya Shah", plan: "Strength · Intermediate", progress: 54, init: "PS" },
    { name: "Rohit Kumar", plan: "Muscle gain · Advanced", progress: 88, init: "RK" },
    { name: "Neha Singh", plan: "Recomp · Intermediate", progress: 41, init: "NS" },
  ];
  return (
    <div className="p-4 space-y-2.5">
      {clients.map((c, i) => (
        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-[#050814] bg-gradient-to-br from-cyan-400 to-blue-500">{c.init}</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">{c.name}</div>
            <div className="text-[10px] text-white/40 truncate">{c.plan}</div>
          </div>
          <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${c.progress}%` }} />
          </div>
          <div className="text-[10px] text-cyan-400 font-bold w-7 text-right">{c.progress}%</div>
        </div>
      ))}
    </div>
  );
}

function WorkoutPanel() {
  const exercises = [
    { name: "Barbell Squat", sets: "4×8", tag: "Legs" },
    { name: "Bench Press", sets: "4×10", tag: "Chest" },
    { name: "Lat Pulldown", sets: "3×12", tag: "Back" },
    { name: "Romanian Deadlift", sets: "3×10", tag: "Hams" },
  ];
  return (
    <div className="p-4">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Monday — Push / Legs</div>
      <div className="space-y-2">
        {exercises.map((e, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="w-7 h-7 rounded-md bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-[10px] font-bold text-cyan-300">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-white truncate">{e.name}</div>
              <div className="text-[10px] text-white/40">{e.sets} · 90s rest</div>
            </div>
            <div className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-white/50">{e.tag}</div>
          </div>
        ))}
      </div>
      <button className="mt-3 w-full text-[11px] font-semibold text-cyan-300 py-2 rounded-lg border border-dashed border-cyan-500/30 hover:bg-cyan-500/5 transition">
        + Add exercise
      </button>
    </div>
  );
}

function DietPanel() {
  const meals = [
    {
      icon: "🍳",
      meal: "Breakfast",
      title: "High-protein breakfast to kickstart fat burning",
      kcal: 420,
    },
    {
      icon: "🥗",
      meal: "Lunch",
      title: "Lean + fibre-rich meal to keep you full till evening",
      kcal: 560,
    },
    {
      icon: "🥜",
      meal: "Snack",
      title: "Smart snack to crush cravings without the crash",
      kcal: 220,
    },
    {
      icon: "🍲",
      meal: "Dinner",
      title: "Light, muscle-sparing dinner for overnight recovery",
      kcal: 480,
    },
  ];
  return (
    <div className="p-4">
      {/* Daily target header */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-white/40">Daily target</div>
        <div className="text-[10px] text-cyan-300 font-bold">1,680 kcal</div>
      </div>

      {/* Macros */}
      <div className="grid grid-cols-3 gap-2 mb-1.5">
        {[
          { l: "Protein", v: "140g" },
          { l: "Carbs", v: "160g" },
          { l: "Fat", v: "55g" },
        ].map((m, i) => (
          <div key={i} className="p-2 rounded-lg bg-white/[0.02] border border-white/5 text-center">
            <div className="text-[9px] text-white/40 uppercase tracking-wider">{m.l}</div>
            <div className="text-xs font-bold text-white">{m.v}</div>
          </div>
        ))}
      </div>

      {/* Tagline under macros */}
      <div className="mb-3 text-[10px] text-cyan-300/90 italic text-center leading-tight">
        Designed to burn fat while keeping muscle intact.
      </div>

      {/* Meal cards */}
      <div className="space-y-2">
        {meals.map((m, i) => (
          <div
            key={i}
            className="flex items-start gap-2.5 p-2.5 rounded-lg bg-gradient-to-br from-white/[0.035] to-white/[0.01] border border-white/5"
          >
            <div className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-base shrink-0">
              <span aria-hidden="true">{m.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[9.5px] text-white/40 uppercase tracking-wider">{m.meal}</div>
                <div className="text-[10px] text-cyan-300 font-bold shrink-0">{m.kcal} kcal</div>
              </div>
              <div className="text-[11.5px] font-semibold text-white leading-snug mt-0.5">
                {m.title}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
